import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Anchor } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { fmtNum } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export default function BookingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rate, setRate] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [bookingRef, setBookingRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: "", contact_person: user?.name || "", email: user?.email || "", phone: "",
    num_containers: Number(params.get("qty")) || 1, cargo_description: "",
    cargo_weight: "", preferred_etd: "", additional_requirements: "",
  });

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, contact_person: f.contact_person || user.name, email: f.email || user.email }));
  }, [user]);

  useEffect(() => {
    const rid = params.get("rate_id");
    if (!rid) { navigate("/rates?shipment_type=export"); return; }
    api.get(`/rates/${rid}`).then(({ data }) => setRate(data)).catch(() => navigate("/rates?shipment_type=export"));
  }, [params]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.company_name || !form.contact_person || !form.phone) {
      toast.error("Company, contact and phone are required.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", { rate_id: rate.id, ...form, num_containers: Number(form.num_containers) });
      setBookingRef(data.booking_ref || "");
      setSubmitted(true);
      toast.success("Booking confirmed — our team will finalize your booking.");
    } catch (e) {
      toast.error("Failed to submit booking. Please try again.");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="h-16 w-16 bg-signal/10 border border-signal/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-8 w-8 text-signal" />
          </div>
          <h1 className="font-heading font-extrabold text-3xl text-navy">Booking Confirmed</h1>
          {bookingRef && (
            <div className="mt-5 inline-flex flex-col items-center gap-1 border border-signal/40 bg-signal/5 rounded-md px-6 py-3" data-testid="booking-ref-box">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Your Booking Reference</span>
              <span className="font-mono-data font-extrabold text-xl text-navy tracking-wider" data-testid="booking-ref-value">{bookingRef}</span>
            </div>
          )}
          <p className="text-muted-foreground mt-3">Thank you. Your booking request has been recorded and the VK Forwarders team will confirm your final booking shortly. Please quote your booking reference in any correspondence. You can track its status in your dashboard.</p>
          <div className="flex gap-3 justify-center mt-8">
            <Button className="bg-signal text-white rounded-sm" onClick={() => navigate("/dashboard")}>View My Bookings</Button>
            <Button variant="outline" className="rounded-sm" onClick={() => navigate("/rates?shipment_type=export")}>Search More Rates</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-navy text-white py-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-signal text-xs font-mono-data mb-1"><ShieldCheck className="h-4 w-4" /> BOOK & FINALIZE RATE</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl">Book This Rate</h1>
          <p className="text-white/60 mt-2">Confirm the rate you want to do business on. This creates a booking our team will finalize.</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 border border-border rounded-md p-6 bg-card space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Company Name *"><Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="rounded-sm" data-testid="bk-company" /></Field>
            <Field label="Contact Person *"><Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} className="rounded-sm" data-testid="bk-contact" /></Field>
            <Field label="Email"><Input value={form.email} disabled className="rounded-sm bg-secondary" data-testid="bk-email" /></Field>
            <Field label="Phone *"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-sm" data-testid="bk-phone" /></Field>
            <Field label="Number of Containers"><Input type="number" min="1" value={form.num_containers} onChange={(e) => set("num_containers", e.target.value)} className="rounded-sm font-mono-data" data-testid="bk-qty" /></Field>
            <Field label="Preferred ETD"><Input type="date" value={form.preferred_etd} onChange={(e) => set("preferred_etd", e.target.value)} className="rounded-sm font-mono-data" data-testid="bk-etd" /></Field>
            <Field label="Cargo Weight"><Input value={form.cargo_weight} onChange={(e) => set("cargo_weight", e.target.value)} placeholder="e.g. 18 MT" className="rounded-sm" data-testid="bk-weight" /></Field>
          </div>
          <Field label="Cargo Description"><Textarea value={form.cargo_description} onChange={(e) => set("cargo_description", e.target.value)} className="rounded-sm" data-testid="bk-cargo" /></Field>
          <Field label="Additional Requirements"><Textarea value={form.additional_requirements} onChange={(e) => set("additional_requirements", e.target.value)} className="rounded-sm" data-testid="bk-additional" /></Field>
          <Button className="w-full bg-signal hover:bg-signal/90 text-white rounded-sm h-11" onClick={submit} disabled={loading || !rate} data-testid="bk-submit">
            <ShieldCheck className="h-4 w-4 mr-2" /> {loading ? "Confirming…" : "Confirm Booking"}
          </Button>
          <p className="text-[11px] text-muted-foreground">Rates are indicative and subject to final confirmation by VK Forwarders at the time of booking.</p>
        </div>

        <div className="lg:col-span-1">
          {rate && (
            <div className="border border-border rounded-md bg-card overflow-hidden sticky top-20">
              <div className="bg-navy text-white p-5"><div className="text-xs uppercase text-white/50">Booking Rate</div><div className="font-heading font-bold text-lg mt-1 flex items-center gap-2"><Anchor className="h-4 w-4 text-signal" />{rate.shipping_line_name}</div></div>
              <div className="p-5 space-y-2.5 text-sm">
                <Row k="Route" v={`${rate.pol_name} → ${rate.pod_name}`} />
                <Row k="Container" v={rate.size_label} />
                <Row k="Ocean Freight" v={`${rate.currency} ${fmtNum(rate.ocean_freight)}`} />
                <Row k="Local Charges" v={`INR ${fmtNum(rate.local_charges_inr)}`} />
                <Row k="Transit" v={`${rate.transit_time} days`} />
                <Row k="Valid Until" v={(rate.valid_until || "").slice(0, 10)} />
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-xs mb-1.5 block text-muted-foreground">{label}</Label>{children}</div>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between border-b border-border pb-2 last:border-0"><span className="text-muted-foreground">{k}</span><span className="font-mono-data text-navy text-right">{v}</span></div>
);
